import React from 'react';

// The only icon family of the admin panel: 24px grid, 1.7px round strokes,
// currentColor, no fill. Every icon is decorative (aria-hidden): the control
// that holds it always carries its own text or aria-label.

const PATHS: Record<string, React.ReactNode> = {
  home: <path d="M3 11l9-7 9 7M5 10v10h14V10M10 20v-6h4v6" />,
  users: (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
      <path d="M16 5.2a3.2 3.2 0 010 5.6M18 14.4c1.8.8 3 2.6 3 5.6" />
    </>
  ),
  modes: (
    <>
      <path d="M4 7h8M18 7h2M4 17h2M12 17h8" />
      <circle cx="15" cy="7" r="2.2" />
      <circle cx="9" cy="17" r="2.2" />
    </>
  ),
  reports: <path d="M5 21V4M5 4h12l-2 4 2 4H5" />,
  broadcast: <path d="M3 10v4h3l8 4V6L6 10zM17.5 9a4 4 0 010 6" />,
  teleport: (
    <>
      <path d="M12 21s7-6.2 7-11a7 7 0 10-14 0c0 4.8 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.4" />
    </>
  ),
  placement: <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3zM4 7.5l8 4.5 8-4.5M12 12v9" />,
  spawns: (
    <>
      <circle cx="12" cy="12" r="7" />
      <circle cx="12" cy="12" r="2.4" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
    </>
  ),
  weather: (
    <>
      <circle cx="8" cy="8.5" r="2.8" />
      <path d="M8 3v1M2.8 8.5h1M4.3 4.8l.7.7M11.7 4.8l-.7.7" />
      <path d="M8.5 20h8.5a3.5 3.5 0 00.3-7 5 5 0 00-9.4 1.4A2.8 2.8 0 008.5 20z" />
    </>
  ),
  housing: <path d="M3 11l9-7 9 7v9H3zM10 20v-5h4v5" />,
  status: <path d="M3 12h4l3-8 4 16 3-8h4" />,
  debug: (
    <>
      <path d="M9 9h6v7a3 3 0 01-6 0z" />
      <path d="M12 5v4M8 6l2 3M16 6l-2 3M5 12h4M15 12h4M5 18l4-2M19 18l-4-2" />
    </>
  ),
  staff: (
    <>
      <path d="M12 3l8 3v6c0 4.5-3.2 8-8 9-4.8-1-8-4.5-8-9V6z" />
      <path d="M8.5 12l2.5 2.5 4.5-5" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="M20 20l-4.5-4.5" />
    </>
  ),
  close: <path d="M6 6l12 12M18 6L6 18" />,
  refresh: <path d="M20 11a8 8 0 10-2.3 5.7M20 5v6h-6" />,
  check: <path d="M5 12.5l4.5 4.5L19 7" />,
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  list: <path d="M4 6h16M4 12h16M4 18h16" />,
  table: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="1.5" />
      <path d="M3 10h18M9 4v16" />
    </>
  ),
  density: <path d="M4 7h16M4 12h16M4 17h16" />,
  copy: (
    <>
      <rect x="9" y="9" width="11" height="11" rx="1.5" />
      <path d="M5 15V5a1 1 0 011-1h10" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4l3 2" />
    </>
  ),
  chevron: <path d="M9 6l6 6-6 6" />,
  grid: (
    <>
      <rect x="4" y="4" width="7" height="7" rx="1" />
      <rect x="13" y="4" width="7" height="7" rx="1" />
      <rect x="4" y="13" width="7" height="7" rx="1" />
      <rect x="13" y="13" width="7" height="7" rx="1" />
    </>
  ),
  journal: <path d="M7 3h10a1 1 0 011 1v16a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1zM9 8h6M9 12h6M9 16h4" />,
  tools: <path d="M14.5 6.5a4 4 0 00-5 5L4 17l3 3 5.5-5.5a4 4 0 005-5l-2.5 2.5-2.5-.5-.5-2.5z" />,
};

export type IconName = keyof typeof PATHS;

export const Icon: React.FC<{ name: string; size?: number; className?: string }> = ({ name, size = 18, className }) => (
  <svg
    className={'admin-panel__icon' + (className ? ' ' + className : '')}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {PATHS[name] || null}
  </svg>
);
